import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, User } from "lucide-react";

function Customer360({ id, onClose }) {
  const { data, isLoading } = useQuery({ queryKey: ["c360", id], queryFn: async () => (await api.get(`/admin/customers/${id}`)).data, enabled: !!id });
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
        {isLoading || !data ? <Loader2 className="w-6 h-6 animate-spin text-teal mx-auto my-8" /> : (
          <>
            <DialogHeader><DialogTitle>{data.customer.name} — Customer 360</DialogTitle></DialogHeader>
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-2 gap-2 text-slate-600">
                <div>Email: <b>{data.customer.email}</b></div>
                <div>Mobile: <b>{data.customer.mobile || "—"}</b></div>
                <div>Orders: <b>{data.orders.length}</b></div>
                <div>Total spend: <b>{inr(data.customer.total_spend || 0)}</b></div>
                <div>Joined: <b>{new Date(data.customer.created_at).toLocaleDateString("en-IN")}</b></div>
                <div>Marketing consent: <b>{data.customer.marketing_consent ? "Yes" : "No"}</b></div>
              </div>
              <div>
                <h4 className="font-semibold text-navy mb-1">Orders</h4>
                {data.orders.map((o) => <div key={o.id} className="flex justify-between text-xs border-b py-1"><span className="font-mono">{o.order_no}</span><span>{inr(o.total)} · {o.status}</span></div>)}
                {!data.orders.length && <p className="text-slate-400 text-xs">No orders</p>}
              </div>
              <div>
                <h4 className="font-semibold text-navy mb-1">Digital entitlements</h4>
                {data.entitlements.map((e) => <div key={e.id} className="text-xs text-slate-600">• {e.product_name} ({e.status})</div>)}
                {!data.entitlements.length && <p className="text-slate-400 text-xs">None</p>}
              </div>
              <div>
                <h4 className="font-semibold text-navy mb-1">Recent activity</h4>
                {data.events.slice(0, 10).map((ev) => <div key={ev.id} className="text-xs text-slate-500">{ev.event_type} · {new Date(ev.created_at).toLocaleDateString("en-IN")}</div>)}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCustomers() {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const { data: customers, isLoading } = useQuery({ queryKey: ["admin-customers", q], queryFn: async () => (await api.get("/admin/customers", { params: { q } })).data });
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Customers</h1>
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-slate-900 border-white/10 text-white" data-testid="customer-search" />
      </div>
      {isLoading ? <Loader2 className="w-8 h-8 animate-spin text-teal" /> : (
        <div className="bg-slate-900 border border-white/10 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase"><tr><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Orders</th><th className="text-left px-4 py-3">Spend</th><th className="text-left px-4 py-3">Joined</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {customers.map((c) => (
                <tr key={c.id} className="hover:bg-white/5 cursor-pointer" onClick={() => setSel(c.id)} data-testid={`customer-${c.email}`}>
                  <td className="px-4 py-3"><div className="flex items-center gap-2 text-white"><User className="w-4 h-4 text-teal-500" /> {c.name}</div><div className="text-xs text-slate-500 ml-6">{c.email}</div></td>
                  <td className="px-4 py-3 text-slate-300">{c.total_orders || 0}</td>
                  <td className="px-4 py-3 text-white">{inr(c.total_spend || 0)}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{new Date(c.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
              {!customers.length && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No customers found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {sel && <Customer360 id={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
